import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  onCategoryClick: () => void;
}

export default function Navbar({ onCategoryClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavArticle = (slug: string) => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
    navigate(`/articolo/${slug}`);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-16">
            <a href="/" className="flex flex-col leading-tight">
              <span className="text-xs tracking-[0.2em] uppercase text-foreground-500 font-label font-medium">
                Itaobuy
              </span>
              <span className="text-sm tracking-[0.15em] uppercase text-foreground-700 font-label font-light">
                Spreadsheet
              </span>
            </a>

            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer font-body whitespace-nowrap">
                Home
              </a>
              <button
                onClick={onCategoryClick}
                className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer font-body whitespace-nowrap"
              >
                Categorie
              </button>
              <a href="/articolo/itaobuy-sheet-guida-acquisto" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer font-body whitespace-nowrap" onClick={(e) => { e.preventDefault(); handleNavArticle('itaobuy-sheet-guida-acquisto'); }}>
                Guida all&apos;Acquisto
              </a>
              <a href="/articolo/itaobuy-sheet-guida-completa" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer font-body whitespace-nowrap" onClick={(e) => { e.preventDefault(); handleNavArticle('itaobuy-sheet-guida-completa'); }}>
                Guida
              </a>
              <a href="#faq" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors font-body whitespace-nowrap">
                FAQ
              </a>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Itaobuy Sheet
                <i className="ri-arrow-right-line ml-1.5"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-700 cursor-pointer relative"
                aria-label="Menu"
              >
                {mobileMenuOpen ? (
                  <i className="ri-close-line text-xl"></i>
                ) : (
                  <i className="ri-menu-line text-xl"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-16 right-0 w-72 max-w-[85vw] bg-background-50 h-[calc(100vh-4rem)] overflow-y-auto border-l border-background-200/60">
            <div className="p-5">
              <div className="mb-6">
                <span className="text-xs tracking-[0.15em] uppercase text-foreground-400 font-label">Menu</span>
              </div>

              <div className="space-y-1 mb-6">
                <a href="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors font-body">
                  <i className="ri-home-line text-foreground-400"></i>
                  Home
                </a>
                <button onClick={() => { setMobileMenuOpen(false); onCategoryClick(); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors font-body text-left">
                  <i className="ri-grid-line text-foreground-400"></i>
                  Categorie Prodotti
                </button>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavArticle('itaobuy-sheet-guida-acquisto'); }} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors font-body">
                  <i className="ri-shopping-bag-3-line text-foreground-400"></i>
                  Guida all&apos;Acquisto
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavArticle('itaobuy-sheet-guida-completa'); }} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors font-body">
                  <i className="ri-book-open-line text-foreground-400"></i>
                  Guida Completa
                </a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors font-body">
                  <i className="ri-question-line text-foreground-400"></i>
                  FAQ
                </a>
              </div>

              <div className="border-t border-background-200/60 pt-4 mb-4">
                <span className="text-xs tracking-[0.15em] uppercase text-foreground-400 font-label">Articoli SEO</span>
              </div>
              <div className="space-y-1 mb-6">
                {[
                  { slug: 'itaobuy-sheet-tutorial', label: 'Tutorial Completo' },
                  { slug: 'itaobuy-sheet-recensione', label: 'Recensione' },
                  { slug: 'itaobuy-sheet-vs-excel', label: 'Itaobuy vs Excel' },
                  { slug: 'itaobuy-sheet-sicurezza', label: 'Sicurezza' },
                  { slug: 'itaobuy-sheet-alternative', label: 'Alternative' },
                  { slug: 'itaobuy-sheet-vantaggi', label: 'Vantaggi' },
                  { slug: 'itaobuy-sheet-prezzo', label: 'Prezzi' },
                  { slug: 'itaobuy-sheet-template', label: 'Template' },
                ].map((item) => (
                  <a key={item.slug} href="#" onClick={(e) => { e.preventDefault(); handleNavArticle(item.slug); }} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors font-body">
                    <i className="ri-article-line text-foreground-400 text-xs"></i>
                    {item.label}
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200/60 pt-5 space-y-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer font-label"
                >
                  Itaobuy Sheet
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer font-label"
                >
                  Buy It Now
                  <i className="ri-shopping-bag-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}